/**
 * listenEbenen.ts — Gliederungszeichen je Listenebene wie in Outlook/Word.
 *
 * T-346 („sowie in Outlook"): Eine Nummerierung zählt auf der ersten Ebene
 * `1.`, eine Ebene tiefer `a.`, darunter `i.`, danach wieder `1.` usw.; eine
 * Aufzählung entsprechend `•` → `◦` → `▪`. Vorher legte der Browser beim
 * Einrücken (Tab) eine verschachtelte Liste DESSELBEN Typs ohne jede Angabe
 * an, und jede Ebene zählte wieder `1., 2., 3.`.
 *
 * ══ WARUM INLINE UND NICHT NUR CSS ═════════════════════════════════════════
 * Eine Stylesheet-Regel (`.rte-body ol ol { … }`) wirkt nur im KMVS-Fenster.
 * Versendet wird der rohe `innerHTML` — ohne Stylesheet; beim Empfänger
 * entschieden sonst die Standardwerte seines Mailprogramms. Deshalb steht das
 * Zeichen als `list-style-type` UND als `type`-Attribut am `<ol>`/`<ul>`:
 * Browser und Outlook Web lesen das CSS, der Word-Renderer von Outlook Desktop
 * zuverlässig das klassische `type`. Der Server-Sanitizer lässt beides durch
 * (`mail-html-sanitizer.ts`: `style` an allen Elementen, `type` an `ol`/`ul`).
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * Die Ebene zählt wie in Word über ALLE Listen: Eine Aufzählung unter einem
 * nummerierten Punkt steht auf Ebene 2 und bekommt `◦`, eine Nummerierung
 * unter einem Spiegelstrich `a.`.
 *
 * Fremd gesetzte Zeichen (etwa eine aus Word eingefügte Liste mit `I.`, `A.`)
 * bleiben stehen — angeglichen wird nur, was keine Angabe trägt oder eine aus
 * dem eigenen Schema (dann war es eine frühere Angleichung auf anderer Ebene).
 */

type Zeichen = { css: string; type: string };

const NUMMERIERUNG: readonly Zeichen[] = [
    { css: "decimal", type: "1" },
    { css: "lower-alpha", type: "a" },
    { css: "lower-roman", type: "i" },
];

const AUFZAEHLUNG: readonly Zeichen[] = [
    { css: "disc", type: "disc" },
    { css: "circle", type: "circle" },
    { css: "square", type: "square" },
];

/** Das Zeichen einer Liste auf Ebene `ebene` (0 = oberste Ebene). */
export function zeichenFuerEbene(tag: "OL" | "UL", ebene: number): Zeichen {
    const schema = tag === "OL" ? NUMMERIERUNG : AUFZAEHLUNG;
    return schema[((ebene % schema.length) + schema.length) % schema.length];
}

/** Trägt die Liste ein Zeichen, das NICHT aus dem eigenen Schema stammt? */
function hatFremdesZeichen(liste: HTMLElement): boolean {
    const schema = liste.tagName === "OL" ? NUMMERIERUNG : AUFZAEHLUNG;
    const css = liste.style.listStyleType.trim().toLowerCase();
    if (css && !schema.some((z) => z.css === css)) return true;
    const type = liste.getAttribute("type");
    // `type` unterscheidet bei `ol` Groß-/Kleinschreibung („A" ≠ „a").
    if (type != null && type !== "" && !schema.some((z) => z.type === type)) return true;
    return false;
}

/** Wie viele Listen (`ol`/`ul`) liegen zwischen `liste` und `root`? */
export function listenEbene(liste: Element, root: Node): number {
    let ebene = 0;
    let n: Node | null = liste.parentNode;
    while (n && n !== root) {
        if (n.nodeType === 1) {
            const tag = (n as Element).tagName;
            if (tag === "OL" || tag === "UL") ebene++;
        }
        n = n.parentNode;
    }
    return ebene;
}

/**
 * Setzt an jeder Liste unter `root` das Zeichen ihrer Ebene.
 *
 * `ausserhalb`: ein Teilbaum, der unangetastet bleibt — der zitierte Verlauf
 * einer Antwort gehört dem Absender der Ursprungsmail.
 *
 * Idempotent; andere Inline-Angaben (Abstand, Schrift) bleiben erhalten.
 * Gibt zurück, ob sich etwas geändert hat.
 */
export function listenEbenenAngleichen(
    root: ParentNode | null,
    opts: { ausserhalb?: Element | null } = {},
): boolean {
    if (!root) return false;
    let geaendert = false;
    root.querySelectorAll<HTMLElement>("ol, ul").forEach((liste) => {
        if (opts.ausserhalb && opts.ausserhalb.contains(liste)) return;
        if (hatFremdesZeichen(liste)) return;
        const z = zeichenFuerEbene(liste.tagName as "OL" | "UL", listenEbene(liste, root as Node));
        if (liste.style.listStyleType !== z.css) {
            liste.style.listStyleType = z.css;
            geaendert = true;
        }
        if (liste.getAttribute("type") !== z.type) {
            liste.setAttribute("type", z.type);
            geaendert = true;
        }
    });
    return geaendert;
}
